import { useCallback, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import { slideReveal } from "./Autopilot.styled";
import { LandscapeMediaItem } from "./LandscapeMediaItem";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import { useAssetById } from "hooks/useAssetById";
import { getAssetUrl } from "pages/media/projects/components/slides/utils";

interface VisualsVisualProps {
  isActive: boolean;
  isDone: boolean;
  onComplete: () => void;
  sections: SectionWithSlides[];
}

interface SlideMediaProps {
  assetId: string;
  mediaType: "image" | "video";
  index: number;
  isDone: boolean;
  onReady: () => void;
}

const SlideMedia: React.FC<SlideMediaProps> = ({
  assetId,
  mediaType,
  index,
  isDone,
  onReady,
}) => {
  const { data: asset } = useAssetById({ assetId });
  const src = getAssetUrl(asset?.data.path);
  return src ? (
    <LandscapeMediaItem
      src={src}
      mediaType={mediaType}
      startDelay={index * 400}
      isDone={isDone}
      onReady={onReady}
    />
  ) : null;
};

export const VisualsVisual: React.FC<VisualsVisualProps> = ({
  isActive,
  isDone,
  onComplete,
  sections,
}) => {
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  const assets = useMemo(() => {
    const mediaItems: { assetId: string; mediaType: "image" | "video" }[] = [];
    for (const section of sections) {
      for (const slide of section.slides) {
        if ("image_element" in slide.placeholders) {
          const id = slide.placeholders.image_element?.asset_id;
          if (id)
            mediaItems.push({
              assetId: id,
              mediaType:
                slide.placeholders.image_element?.media_type ?? "image",
            });
        }
      }
    }
    return mediaItems;
  }, [sections]);

  const readyCount = useRef(0);

  const handleReady = useCallback(() => {
    readyCount.current += 1;
    if (readyCount.current >= assets.length) {
      onCompleteRef.current();
    }
  }, [assets.length]);

  if (!isActive && !isDone) return null;
  if (assets.length === 0) return null;

  return (
    <VisualsRow>
      {assets.map(({ assetId, mediaType }, i) => {
        return (
          <SlideMedia
            key={`${assetId}-${mediaType}`}
            assetId={assetId}
            mediaType={mediaType}
            index={i}
            isDone={isDone}
            onReady={handleReady}
          />
        );
      })}
    </VisualsRow>
  );
};

const VisualsRow = styled.div`
  padding: 14px 15px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  animation: ${slideReveal} 0.3s ease both;
`;
